import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Sidebar, formatCurrency } from "@/components/Layout";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import EditTransactionSheet from "@/components/EditTransactionSheet";
import BehaviorAlerts from "@/components/BehaviorAlerts";
import UpgradeModal, { TRIGGER_SOURCES } from "@/components/UpgradeModal";
import { usePaywall, LockedFeature } from "@/components/PaywallContext";
import GuidedHighlight from "@/components/GuidedHighlight";
import { formatDateLocal, parseDateLocal, getTodayStr } from "@/lib/utils";

// Extracted calendar components
import {
  FinancialSummary,
  StartingBalanceInput,
  SmartGuidanceBubble,
  QuickPreviewCard,
  FullDayBreakdown
} from "@/components/calendar";
import SafeToSpendCard, { getUnifiedStatus } from "@/components/SafeToSpendCard";
import UnpaidBillsModal from "@/components/UnpaidBillsModal";
import OverdueConfirmationModal from "@/components/OverdueConfirmationModal";
import RunOutCard from "@/components/RunOutCard";
import SurvivalModeToggle from "@/components/SurvivalModeToggle";

import { 
  ChevronLeft, 
  ChevronRight, 
  Plus,
  TrendingUp,
  TrendingDown,
  CreditCard,
  CheckCircle,
  AlertCircle,
  Calendar as CalendarIcon,
  BarChart3,
  Crown,
  Lock,
  ArrowRight
} from "lucide-react";

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

// ============== MAIN CALENDAR PAGE ==============

export default function CalendarPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { triggerSessionBased, isPremium: checkPremiumStatus, showPaywall } = usePaywall();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarData, setCalendarData] = useState({ events: [], day_balances: {} });
  const [loading, setLoading] = useState(true);
  const [forceShowGuide, setForceShowGuide] = useState(false);
  
  // Check for guide replay from URL
  useEffect(() => {
    if (searchParams.get('guide') === '1') {
      setForceShowGuide(true);
      // Remove the query param
      searchParams.delete('guide');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  
  // Interaction state
  const [hoveredDate, setHoveredDate] = useState(null);
  const [previewDate, setPreviewDate] = useState(null);
  const [previewPosition, setPreviewPosition] = useState({ x: 0, y: 0 });
  const [showPreview, setShowPreview] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  
  // Financial status
  const [amIOkayData, setAmIOkayData] = useState(null);
  const [startingBalance, setStartingBalance] = useState(() => {
    // First try localStorage for immediate value, will be updated from server
    const saved = localStorage.getItem('p2p_starting_balance');
    return saved ? parseFloat(saved) : 0;
  });
  
  // Load starting balance from user profile on mount
  useEffect(() => {
    if (user && user.starting_balance !== undefined && user.starting_balance !== null) {
      setStartingBalance(user.starting_balance);
      localStorage.setItem('p2p_starting_balance', user.starting_balance.toString());
    }
  }, [user]);
  
  // Smart guidance
  const [guidance, setGuidance] = useState(null);
  const [guidanceShown, setGuidanceShown] = useState(false);
  
  // Edit transaction state
  const [editSheetOpen, setEditSheetOpen] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [editingTransactionType, setEditingTransactionType] = useState(null);
  
  // Unpaid Bills Modal state
  const [unpaidBillsModalOpen, setUnpaidBillsModalOpen] = useState(false);
  
  // Overdue Confirmation Modal state
  const [overdueModalOpen, setOverdueModalOpen] = useState(false);
  
  // SafeToSpend refresh key
  const [safeToSpendKey, setSafeToSpendKey] = useState(0);
  
  // Run out date data for calendar marker
  const [runOutDate, setRunOutDate] = useState(null);
  
  // Survival mode state
  const [survivalMode, setSurvivalMode] = useState(() => {
    return localStorage.getItem('p2p_survival_mode') === 'true';
  });
  
  // Unified financial status (from SafeToSpendCard)
  const [unifiedStatus, setUnifiedStatus] = useState(null);
  
  // View state
  const [view, setView] = useState('calendar');
  const [forecastMonths, setForecastMonths] = useState('3');
  const [forecastData, setForecastData] = useState(null);
  const [forecastLoading, setForecastLoading] = useState(false);
  const [forecastUpgradeOpen, setForecastUpgradeOpen] = useState(false);
  
  // Refs
  const calendarRef = useRef(null);
  const hoverTimeoutRef = useRef(null);
  const safeToSpendRef = useRef(null);
  
  // Open Unpaid Bills Modal handler (for BehaviorAlerts)
  const openUnpaidBillsModal = useCallback(() => {
    setUnpaidBillsModalOpen(true);
  }, []);
  
  // Open Overdue Confirmation Modal handler (for BehaviorAlerts)
  const openOverdueModal = useCallback(() => {
    setOverdueModalOpen(true);
  }, []);
  
  // Handle overdue confirmation complete - refresh Safe to Spend
  const handleOverdueConfirmed = useCallback(() => {
    setSafeToSpendKey(prev => prev + 1);
  }, []);
  
  // Mobile detection
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const currency = user?.currency || 'GBP';
  const isPremium = user?.is_premium || false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Save starting balance to localStorage AND backend
  const handleStartingBalanceChange = async (value) => {
    setStartingBalance(value);
    localStorage.setItem('p2p_starting_balance', value.toString());
    
    // Persist to backend
    try {
      await axios.put(`${API}/users/settings`, { starting_balance: value }, { withCredentials: true });
    } catch (error) {
      console.error('Error saving starting balance:', error);
    }
  };

  // Fetch calendar data
  useEffect(() => {
    const fetchCalendarData = async () => {
      setLoading(true);
      try {
        const month = currentDate.getMonth() + 1;
        const year = currentDate.getFullYear();
        const response = await axios.get(
          `${API}/dashboard/calendar-enhanced?month=${month}&year=${year}&starting_balance=${startingBalance}`, 
          { withCredentials: true }
        );
        setCalendarData(response.data);
      } catch (error) {
        console.error('Error fetching calendar data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCalendarData();
  }, [currentDate, startingBalance]);

  // Trigger session-based paywall for returning users
  useEffect(() => {
    triggerSessionBased();
  }, [triggerSessionBased]);

  // Generate smart guidance - defined as function declaration for hoisting
  function generateGuidance(data) {
    if (!data) return;
    
    const { risk_days, daily_simulation } = data;
    
    // Find the most important event to highlight
    let guidanceData = null;
    
    // Priority 1: First risk day (only if there are actual risk days)
    if (risk_days && risk_days.length > 0) {
      const firstRisk = risk_days[0];
      const riskDate = parseDateLocal(firstRisk.date);
      const formattedDate = riskDate.toLocaleDateString('en-GB', { 
        weekday: 'long', 
        day: 'numeric', 
        month: 'long' 
      });
      guidanceData = {
        date: firstRisk.date,
        dateObj: riskDate,
        message: `You may be short by ${formatCurrency(Math.abs(firstRisk.shortage), currency)} on ${formattedDate}`,
        cta: 'View day',
        type: 'risk',
        shortage: firstRisk.shortage
      };
    } 
    // Priority 2: Biggest outgoing day (only if no risk days)
    else if (daily_simulation) {
      const biggestOutgoing = daily_simulation
        .filter(d => d.outgoing > 0)
        .sort((a, b) => b.outgoing - a.outgoing)[0];
      
      if (biggestOutgoing && biggestOutgoing.outgoing > 100) {
        const outDate = parseDateLocal(biggestOutgoing.date);
        const formattedDate = outDate.toLocaleDateString('en-GB', { 
          weekday: 'long', 
          day: 'numeric', 
          month: 'long' 
        });
        guidanceData = {
          date: biggestOutgoing.date,
          dateObj: outDate,
          message: `${formatCurrency(biggestOutgoing.outgoing, currency)} going out on ${formattedDate}`,
          cta: 'View day',
          type: 'info'
        };
      }
    }
    
    if (guidanceData) {
      setGuidance(guidanceData);
      setGuidanceShown(true);
    } else {
      // No guidance needed
      setGuidance(null);
      setGuidanceShown(true); // Mark as shown to prevent re-checking
    }
  }

  // Fetch "Am I Okay?" status
  useEffect(() => {
    const fetchAmIOkay = async () => {
      try {
        const response = await axios.get(
          `${API}/dashboard/am-i-okay?days=7&starting_balance=${startingBalance}`,
          { withCredentials: true }
        );
        setAmIOkayData(response.data);
        
        // Reset guidance when starting balance changes
        setGuidanceShown(false);
        setGuidance(null);
        
        // Generate smart guidance from the data
        if (response.data) {
          generateGuidance(response.data);
        }
      } catch (error) {
        console.error('Error fetching Am I Okay status:', error);
      }
    };
    fetchAmIOkay();
  }, [startingBalance]);

  // Handle clicking on guidance bubble to navigate to that date
  const handleGuidanceClick = useCallback((guidanceData) => {
    if (!guidanceData || !guidanceData.dateObj) return;
    
    // Track event
    axios.post(`${API}/track/event`, {
      event_name: 'shortfall_alert_clicked',
      metadata: { 
        date: guidanceData.date, 
        type: guidanceData.type,
        shortage: guidanceData.shortage 
      }
    }, { withCredentials: true }).catch(() => {});
    
    // Navigate to that month if needed
    const targetDate = new Date(guidanceData.dateObj);
    if (targetDate.getMonth() !== currentDate.getMonth() || targetDate.getFullYear() !== currentDate.getFullYear()) {
      setCurrentDate(new Date(targetDate.getFullYear(), targetDate.getMonth(), 1));
    }
    
    // Select that date to open breakdown
    setSelectedDate(targetDate);
    
    // Open the sheet to show day breakdown
    setSheetOpen(true);
    
    // Dismiss guidance
    setGuidance(null);
    
    // Scroll to calendar if needed
    setTimeout(() => {
      if (calendarRef.current) {
        calendarRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  }, [currentDate]);

  // Fetch forecast
  useEffect(() => {
    const fetchForecast = async () => {
      if (view !== 'forecast') return;
      setForecastLoading(true);
      try {
        const response = await axios.get(`${API}/dashboard/forecast?months=${forecastMonths}`, { withCredentials: true });
        setForecastData(response.data);
      } catch (error) {
        console.error('Error fetching forecast:', error);
      } finally {
        setForecastLoading(false);
      }
    };
    fetchForecast();
  }, [view, forecastMonths]);

  // Group events by date
  const eventsByDate = useMemo(() => {
    return calendarData.events.reduce((acc, event) => {
      if (!acc[event.date]) acc[event.date] = [];
      acc[event.date].push(event);
      return acc;
    }, {});
  }, [calendarData.events]);

  // Calendar grid
  const calendarGrid = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startPadding = firstDay.getDay();
    const totalDays = lastDay.getDate();
    
    const days = [];
    const prevMonth = new Date(year, month, 0);
    for (let i = startPadding - 1; i >= 0; i--) {
      days.push({ date: new Date(year, month - 1, prevMonth.getDate() - i), isCurrentMonth: false });
    }
    for (let i = 1; i <= totalDays; i++) {
      days.push({ date: new Date(year, month, i), isCurrentMonth: true });
    }
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
      days.push({ date: new Date(year, month + 1, i), isCurrentMonth: false });
    }
    return days;
  }, [currentDate]);

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const getDayData = useCallback((date) => {
    const dateStr = formatDateLocal(date);
    const events = eventsByDate[dateStr] || [];
    
    const income = events.filter(e => e.type === 'income');
    const bills = events.filter(e => e.type === 'expense' && !e.is_debt);
    const debt = events.filter(e => e.type === 'expense' && e.is_debt);
    
    const totalOut = [...bills, ...debt].reduce((sum, e) => sum + e.amount, 0);
    const totalIn = income.reduce((sum, e) => sum + e.amount, 0);
    
    const dayBalance = calendarData.day_balances?.[dateStr];
    const afterThisDay = dayBalance?.after_this_day;
    const dayStatus = dayBalance?.status || 'safe';
    
    return { income, bills, debt, totalOut, totalIn, events, balance: totalIn - totalOut, afterThisDay, dayStatus };
  }, [eventsByDate, calendarData.day_balances]);

  const isToday = (date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d.getTime() === today.getTime();
  };

  const isSelected = (date) => selectedDate && date.toDateString() === selectedDate.toDateString();
  const isHovered = (date) => hoveredDate && date.toDateString() === hoveredDate.toDateString();

  // Hover handling
  const handleMouseEnter = (date, event) => {
    if (isMobile) return;
    setHoveredDate(date);
    
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    
    const rect = event.currentTarget.getBoundingClientRect();
    hoverTimeoutRef.current = setTimeout(() => {
      let x = rect.right + 10;
      let y = rect.top;
      if (x + 320 > window.innerWidth) x = rect.left - 330;
      if (y + 350 > window.innerHeight) y = window.innerHeight - 360;
      if (y < 10) y = 10;
      
      setPreviewDate(date);
      setPreviewPosition({ x, y });
      setShowPreview(true);
    }, 150);
  };

  const handleMouseLeave = () => {
    if (isMobile) return;
    setHoveredDate(null);
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    hoverTimeoutRef.current = setTimeout(() => {
      setShowPreview(false);
      setPreviewDate(null);
    }, 100);
  };

  const handleDateClick = (date) => {
    if (isMobile) {
      if (!previewDate || previewDate.toDateString() !== date.toDateString()) {
        setPreviewDate(date);
        setShowPreview(true);
      } else {
        handleViewDetails();
      }
    } else {
      setSelectedDate(date);
      setShowPreview(false);
      setPreviewDate(null);
      setSheetOpen(true);
    }
  };

  const handleViewDetails = () => {
    setSelectedDate(previewDate);
    setShowPreview(false);
    setSheetOpen(true);
  };

  const handleClosePreview = () => {
    setShowPreview(false);
    setPreviewDate(null);
  };

  const handleAddTransaction = () => {
    setSheetOpen(false);
    navigate('/add');
  };

  // Handle edit transaction
  const handleEditTransaction = (transaction, type) => {
    setEditingTransaction(transaction);
    setEditingTransactionType(type === 'debt' ? 'bill' : type); // debt uses bill API
    setEditSheetOpen(true);
  };

  // Refresh all data after edit/delete
  const refreshAllData = async () => {
    try {
      // Refetch calendar data
      const calRes = await axios.get(
        `${API}/dashboard/calendar-enhanced?month=${currentDate.getMonth() + 1}&year=${currentDate.getFullYear()}&starting_balance=${startingBalance}`,
        { withCredentials: true }
      );
      setCalendarData(calRes.data);
      
      // Refetch Am I Okay data and regenerate guidance
      const okRes = await axios.get(
        `${API}/dashboard/am-i-okay?days=7&starting_balance=${startingBalance}`,
        { withCredentials: true }
      );
      setAmIOkayData(okRes.data);
      
      // Clear and regenerate guidance with fresh data
      setGuidance(null);
      setGuidanceShown(false);
      
      // Update selected day data if a day is selected
      if (selectedDate) {
        setSelectedDate(new Date(selectedDate)); // Force re-render
      }
      
      // Trigger SafeToSpendCard refresh via key change
      setSafeToSpendKey(prev => prev + 1);
    } catch (error) {
      console.error('Error refreshing data:', error);
    }
  };

  // Handle marking a bill/debt as paid - opens modal
  const handleMarkPaid = async (item, type, occurrenceDate) => {
    // Open the Unpaid Bills Modal instead of directly marking
    setUnpaidBillsModalOpen(true);
  };
  
  // Handle direct mark paid (called from modal)
  const handleDirectMarkPaid = async (item, type, occurrenceDate) => {
    try {
      if (item.is_paid) {
        // Unmark as paid
        await axios.delete(`${API}/payments/${item.bill_id}/${occurrenceDate}`, { withCredentials: true });
        // Track event
        await axios.post(`${API}/track/event`, {
          event_name: 'bill_marked_unpaid',
          metadata: { bill_id: item.bill_id, occurrence_date: occurrenceDate, type }
        }, { withCredentials: true });
        toast.success('Payment unmarked');
      } else {
        // Mark as paid
        await axios.post(`${API}/payments`, {
          bill_id: item.bill_id,
          occurrence_date: occurrenceDate,
          amount_paid: item.amount
        }, { withCredentials: true });
        // Track event
        await axios.post(`${API}/track/event`, {
          event_name: 'bill_marked_paid',
          metadata: { bill_id: item.bill_id, occurrence_date: occurrenceDate, type }
        }, { withCredentials: true });
        toast.success('Marked as paid');
      }
      
      // Refresh all data
      await refreshAllData();
    } catch (error) {
      console.error('Error toggling paid status:', error);
      toast.error('Failed to update payment status');
    }
  };

  const previewDayData = previewDate ? getDayData(previewDate) : null;
  const selectedDayData = selectedDate ? getDayData(selectedDate) : null;

  return (
    <Sidebar>
      {/* View Toggle */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex bg-white rounded-xl p-1 border border-p2p-border/50 shadow-sm">
          <button
            onClick={() => setView('calendar')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              view === 'calendar' ? 'bg-p2p-teal text-white shadow-sm' : 'text-p2p-muted hover:text-p2p-teal'
            }`}
            data-testid="view-calendar-btn"
          >
            <CalendarIcon className="w-4 h-4" />
            Calendar
          </button>
          <button
            onClick={() => {
              // Check if user has premium for forecast
              if (!checkPremiumStatus()) {
                showPaywall("forecast_locked", "Cash Flow Forecast");
                return;
              }
              setView('forecast');
            }}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              view === 'forecast' ? 'bg-p2p-teal text-white shadow-sm' : 'text-p2p-muted hover:text-p2p-teal'
            }`}
            data-testid="view-forecast-btn"
          >
            <BarChart3 className="w-4 h-4" />
            Forecast
            {!checkPremiumStatus() && <Lock className="w-3 h-3 ml-1 text-amber-400" />}
          </button>
        </div>
        
        <Button 
          className="btn-gold rounded-full h-10 px-5 shadow-md"
          onClick={() => navigate('/add')}
          data-testid="add-transaction-btn"
          data-guide="add-button"
        >
          <Plus className="w-4 h-4 mr-2" /> Add
        </Button>
      </div>

      {view === 'calendar' ? (
        <>
          {/* Starting Balance Input */}
          <StartingBalanceInput 
            value={startingBalance}
            onChange={handleStartingBalanceChange}
            currency={currency}
          />

          {/* Behavior-Based Alerts - Only show highest priority */}
          <BehaviorAlerts 
            className="mb-4" 
            showOnlyOne={true} 
            onOpenUnpaidModal={openUnpaidBillsModal}
            onOpenOverdueModal={openOverdueModal}
            survivalMode={survivalMode}
            unifiedStatus={unifiedStatus}
            startingBalance={startingBalance}
            data-guide="alerts"
          />

          {/* Safe to Spend Card - Hero */}
          <SafeToSpendCard 
            key={safeToSpendKey} 
            className="mb-3"
            startingBalance={startingBalance}
            onStatusChange={setUnifiedStatus}
            onViewShortfall={() => {
              // Scroll to RunOutCard or expand it
              const runOutCard = document.querySelector('[data-testid="run-out-card"]');
              if (runOutCard) {
                runOutCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }
            }}
          />
          
          {/* Cash Flow Forecast Card - RunOutCard handles its own data fetching */}
          <RunOutCard 
            refreshKey={safeToSpendKey}
            startingBalance={startingBalance}
            className="mb-4"
            onDataLoaded={(data) => setRunOutDate(data?.run_out_date)}
          />

          {/* Clean Financial Summary - Use monthly data from calendarData */}
          <FinancialSummary 
            summary={calendarData?.summary}
            currency={currency}
          />

          {/* Month Header */}
          <div className="flex items-center justify-center mb-5">
            <div className="flex items-center gap-3">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => navigateMonth(-1)}
                className="text-p2p-muted hover:text-p2p-teal hover:bg-p2p-teal/5 w-10 h-10 rounded-xl"
                data-testid="prev-month-btn"
              >
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <h1 className="font-manrope text-2xl font-bold text-p2p-teal min-w-[200px] text-center">
                {MONTHS[currentDate.getMonth()]} {currentDate.getFullYear()}
              </h1>
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => navigateMonth(1)}
                className="text-p2p-muted hover:text-p2p-teal hover:bg-p2p-teal/5 w-10 h-10 rounded-xl"
                data-testid="next-month-btn"
              >
                <ChevronRight className="w-5 h-5" />
              </Button>
            </div>
          </div>

          {/* Calendar Grid */}
          <div ref={calendarRef} className="relative rounded-2xl overflow-hidden shadow-lg border border-p2p-border/50 bg-white" data-testid="calendar-grid" data-guide="calendar">
            {/* Weekday Headers */}
            <div className="grid grid-cols-7 bg-p2p-surface-hover border-b border-p2p-border/50">
              {WEEKDAYS.map(day => (
                <div key={day} className="py-3 text-center text-[11px] font-bold text-p2p-muted uppercase tracking-wider">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Days */}
            {loading ? (
              <div className="flex items-center justify-center h-80">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-p2p-teal border-t-transparent"></div>
              </div>
            ) : (
              <div className="grid grid-cols-7 relative">
                {/* Smart Guidance Bubble */}
                {guidance && (
                  <SmartGuidanceBubble 
                    guidance={guidance}
                    onDismiss={() => setGuidance(null)}
                    onClick={handleGuidanceClick}
                  />
                )}
                
                {calendarGrid.map((day, index) => {
                  const dayData = getDayData(day.date);
                  const hasIncome = dayData.income.length > 0;
                  const hasBills = dayData.bills.length > 0;
                  const hasDebt = dayData.debt.length > 0;
                  const hasEvents = dayData.events.length > 0;
                  const todayDate = isToday(day.date);
                  const selected = isSelected(day.date);
                  const hovered = isHovered(day.date);
                  
                  const afterThisDay = dayData.afterThisDay;
                  const isRiskDay = afterThisDay !== undefined && afterThisDay < 0;
                  const isTightDay = afterThisDay !== undefined && afterThisDay >= 0 && afterThisDay <= 100;
                  
                  // Get local date string for comparisons
                  const dayDateStr = formatDateLocal(day.date);
                  
                  // Is this the guidance target?
                  const isGuidanceTarget = guidance && guidance.date === dayDateStr;
                  
                  // Is this the run out date?
                  const isRunOutDate = runOutDate && dayDateStr === runOutDate;
                  
                  return (
                    <div
                      key={dayDateStr}
                      onClick={() => day.isCurrentMonth && handleDateClick(day.date)}
                      onMouseEnter={(e) => day.isCurrentMonth && handleMouseEnter(day.date, e)}
                      onMouseLeave={handleMouseLeave}
                      className={`
                        relative min-h-[80px] sm:min-h-[90px] border-b border-r border-p2p-border/30
                        ${day.isCurrentMonth ? 'cursor-pointer' : 'cursor-default bg-gray-50/50'}
                        ${hovered && !selected && day.isCurrentMonth ? 'bg-p2p-teal/5' : ''}
                        ${day.isCurrentMonth && isRiskDay && hasEvents && !selected ? 'bg-red-50/70' : ''}
                        ${day.isCurrentMonth && isTightDay && hasEvents && !isRiskDay && !selected ? 'bg-amber-50/50' : ''}
                      `}
                      data-testid={`day-${dayDateStr}`}
                    >
                      {/* Inner wrapper for selected state - ensures complete border visibility */}
                      <div 
                        className={`
                          relative w-full h-full p-2 transition-all duration-200
                          ${selected && !isRiskDay ? 'z-20 ring-2 ring-inset ring-p2p-teal bg-p2p-teal/10 shadow-lg shadow-p2p-teal/20 rounded-sm' : ''}
                          ${selected && isRiskDay ? 'z-20 ring-2 ring-inset ring-red-500 bg-red-50 shadow-lg shadow-red-500/20 rounded-sm' : ''}
                          ${isGuidanceTarget && !selected ? 'ring-2 ring-inset ring-p2p-teal rounded-sm' : ''}
                        `}
                        style={{ minHeight: 'inherit' }}
                      >
                        {/* Risk Day Warning Icon */}
                        {hasEvents && day.isCurrentMonth && isRiskDay && (
                          <div className="absolute top-1 right-1 w-5 h-5 rounded-full bg-red-500 flex items-center justify-center animate-pulse z-10">
                            <AlertCircle className="w-3 h-3 text-white" />
                          </div>
                        )}
                        
                        {/* Tight Day Indicator */}
                        {hasEvents && day.isCurrentMonth && isTightDay && !isRiskDay && (
                          <div className="absolute top-1 right-1 w-3 h-3 rounded-full bg-amber-400 animate-pulse z-10" />
                        )}
                        
                        {/* Cash Flow Warning Marker */}
                        {day.isCurrentMonth && isRunOutDate && !isRiskDay && (
                          <div className="absolute top-1 left-1 px-1.5 py-0.5 rounded bg-amber-500/90 text-[8px] font-bold text-white shadow-sm z-10">
                            LOW
                          </div>
                        )}
                        
                        {/* Date Number - Enhanced selected state */}
                        <div className={`
                          w-7 h-7 rounded-lg text-xs font-semibold flex items-center justify-center mb-1 transition-all
                          ${todayDate ? 'bg-p2p-teal text-white shadow-sm' : ''}
                          ${selected && !todayDate && !isRiskDay ? 'bg-p2p-teal/20 text-p2p-teal font-bold ring-1 ring-p2p-teal/50' : ''}
                          ${selected && isRiskDay && !todayDate ? 'bg-red-100 text-red-600 font-bold' : ''}
                          ${!todayDate && !selected && day.isCurrentMonth ? 'text-p2p-teal/70' : ''}
                          ${!day.isCurrentMonth ? 'text-gray-300' : ''}
                        `}>
                          {day.date.getDate()}
                        </div>
                        
                        {/* Event Indicators */}
                        {hasEvents && day.isCurrentMonth && (
                          <div className="flex gap-1 mb-1">
                            {hasIncome && <div className="w-2 h-2 rounded-full bg-emerald-500" />}
                            {hasBills && <div className="w-2 h-2 rounded-full bg-red-500" />}
                            {hasDebt && <div className="w-2 h-2 rounded-full bg-purple-500" />}
                          </div>
                        )}
                        
                        {/* Daily Outgoing Total */}
                        {dayData.totalOut > 0 && day.isCurrentMonth && (
                          <div className="text-[10px] font-mono text-p2p-muted/70">
                            {formatCurrency(dayData.totalOut, currency)}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Legend */}
            <div className="flex items-center justify-center gap-6 py-3 bg-gray-50/50 border-t border-p2p-border/30">
              <div className="flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                <span className="text-xs text-p2p-muted">Income</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-red-500" />
                <span className="text-xs text-p2p-muted">Bills</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-purple-500" />
                <span className="text-xs text-p2p-muted">Debt</span>
              </div>
            </div>
          </div>
          
          {/* Survival Mode Toggle - Below Calendar */}
          <SurvivalModeToggle 
            className="mt-4"
            onToggle={setSurvivalMode}
          />

          {/* Desktop Hover Preview */}
          {showPreview && previewDate && previewDayData && !isMobile && (
            <div
              onMouseEnter={() => hoverTimeoutRef.current && clearTimeout(hoverTimeoutRef.current)}
              onMouseLeave={handleMouseLeave}
            >
              <QuickPreviewCard
                dayData={previewDayData}
                date={previewDate}
                currency={currency}
                position={previewPosition}
                onViewDetails={handleViewDetails}
                onClose={handleClosePreview}
                isMobile={false}
                afterThisDay={previewDayData.afterThisDay}
              />
            </div>
          )}

          {/* Mobile Preview */}
          {showPreview && previewDate && previewDayData && isMobile && (
            <>
              <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40" onClick={handleClosePreview} />
              <QuickPreviewCard
                dayData={previewDayData}
                date={previewDate}
                currency={currency}
                position={previewPosition}
                onViewDetails={handleViewDetails}
                onClose={handleClosePreview}
                isMobile={true}
                afterThisDay={previewDayData.afterThisDay}
              />
            </>
          )}

          {/* Full Day Breakdown */}
          {/* Full Day Breakdown */}
          <FullDayBreakdown
            isOpen={sheetOpen}
            onClose={() => { setSheetOpen(false); setSelectedDate(null); }}
            dayData={selectedDayData}
            date={selectedDate}
            currency={currency}
            onAddTransaction={handleAddTransaction}
            onEditTransaction={handleEditTransaction}
            onMarkPaid={handleMarkPaid}
            isMobile={isMobile}
            afterThisDay={selectedDayData?.afterThisDay}
            isRunOutDate={selectedDate && runOutDate && formatDateLocal(selectedDate) === runOutDate}
          />

          {/* Edit Transaction Sheet */}
          <EditTransactionSheet
            isOpen={editSheetOpen}
            onClose={() => {
              setEditSheetOpen(false);
              setEditingTransaction(null);
              setEditingTransactionType(null);
            }}
            transaction={editingTransaction}
            transactionType={editingTransactionType}
            currency={currency}
            onUpdate={refreshAllData}
            isMobile={isMobile}
          />
        </>
      ) : (
        /* Forecast View */
        <div className="space-y-5">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="font-manrope text-xl font-semibold text-p2p-teal">Cash Flow Forecast</h1>
              <p className="text-sm text-p2p-muted">See where your finances are heading</p>
            </div>
            
            <Select value={forecastMonths} onValueChange={setForecastMonths}>
              <SelectTrigger className="w-32 h-10 bg-white border-p2p-border">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="3">3 months</SelectItem>
                <SelectItem value="6">6 months</SelectItem>
                <SelectItem value="12" disabled={!isPremium}>
                  <div className="flex items-center gap-2">
                    12 months {!isPremium && <Crown className="w-3 h-3 text-p2p-gold" />}
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {forecastLoading ? (
            <div className="flex items-center justify-center h-40">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-p2p-teal border-t-transparent"></div>
            </div>
          ) : forecastData ? (
            <div className="space-y-3" data-testid="forecast-list">
              {forecastData.forecast.map((month, index) => {
                // Generate guidance message based on financial status
                const getGuidanceMessage = () => {
                  if (month.left_over < 0) {
                    const shortfall = Math.abs(month.left_over);
                    // More actionable guidance
                    const dailyReduction = (shortfall / 30).toFixed(0);
                    return {
                      type: 'warning',
                      message: `Reduce £${dailyReduction}/day OR increase income OR delay a bill to cover this shortfall.`
                    };
                  } else if (month.left_over > 0 && month.left_over < 100) {
                    return {
                      type: 'tight',
                      message: `Tight month ahead. You'll have ${formatCurrency(month.left_over, currency)} left over.`
                    };
                  } else if (month.left_over >= 100) {
                    return {
                      type: 'good',
                      message: `Looking good! Consider putting extra towards debt or savings.`
                    };
                  }
                  return null;
                };
                
                const guidance = getGuidanceMessage();
                
                // For free users, blur months after the first one
                const isLocked = !isPremium && index > 0;
                
                return (
                <div 
                  key={`${month.year}-${month.month}`}
                  className={`bg-white rounded-xl border border-p2p-border/50 shadow-sm overflow-hidden ${
                    month.is_negative ? 'border-l-4 border-l-red-400' : ''
                  } ${isLocked ? 'relative' : ''}`}
                >
                  {/* Locked overlay for free users */}
                  {isLocked && (
                    <div className="absolute inset-0 bg-white/80 backdrop-blur-sm z-10 flex items-center justify-center">
                      <div className="text-center px-4">
                        <Lock className="w-6 h-6 text-p2p-muted mx-auto mb-2" />
                        <p className="text-sm text-p2p-muted font-medium">Month {index + 1} locked</p>
                      </div>
                    </div>
                  )}
                  
                  <div className={`p-4 ${isLocked ? 'blur-sm select-none' : ''}`}>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                          month.is_negative ? 'bg-red-100' : index === 0 ? 'bg-p2p-teal/10' : 'bg-gray-100'
                        }`}>
                          <span className={`text-sm font-semibold ${
                            month.is_negative ? 'text-red-500' : index === 0 ? 'text-p2p-teal' : 'text-p2p-muted'
                          }`}>
                            {month.month_name.slice(0, 3)}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-p2p-teal">{month.month_name} {month.year}</p>
                          <div className="flex items-center gap-2">
                            {index === 0 && <span className="text-xs text-p2p-muted">Current month</span>}
                            {month.is_negative && (
                              <span className="text-xs text-red-500 font-medium flex items-center gap-1">
                                <AlertCircle className="w-3 h-3" /> Shortfall expected
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-3 gap-4 text-right">
                        <div>
                          <p className="text-xs text-p2p-muted mb-0.5">Income</p>
                          <p className="font-mono text-sm font-semibold text-emerald-500">{formatCurrency(month.income, currency)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-p2p-muted mb-0.5">Outgoing</p>
                          <p className="font-mono text-sm font-semibold text-red-500">{formatCurrency(month.outgoing, currency)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-p2p-muted mb-0.5">Left Over</p>
                          <p className={`font-mono text-sm font-semibold ${month.left_over >= 0 ? 'text-p2p-teal' : 'text-red-500'}`}>
                            {month.left_over < 0 && '-'}{formatCurrency(Math.abs(month.left_over), currency)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Guidance Message */}
                  {guidance && !isLocked && (
                    <div className={`px-4 py-3 border-t ${
                      guidance.type === 'warning' ? 'bg-red-50 border-red-100' :
                      guidance.type === 'tight' ? 'bg-amber-50 border-amber-100' :
                      'bg-emerald-50 border-emerald-100'
                    }`}>
                      <p className={`text-sm ${
                        guidance.type === 'warning' ? 'text-red-700' :
                        guidance.type === 'tight' ? 'text-amber-700' :
                        'text-emerald-700'
                      }`}>
                        {guidance.message}
                      </p>
                    </div>
                  )}
                </div>
              )})}

              {/* Upgrade CTA for free users */}
              {!isPremium && forecastData.forecast.length > 1 && (
                <div 
                  onClick={() => setForecastUpgradeOpen(true)}
                  className="bg-gradient-to-r from-p2p-gold/5 to-transparent rounded-xl border-2 border-dashed border-p2p-gold/30 p-6 cursor-pointer hover:border-p2p-gold/50 transition-all"
                  data-testid="forecast-upgrade-cta"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-p2p-gold/20 flex items-center justify-center">
                      <CalendarIcon className="w-6 h-6 text-p2p-gold" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-outfit font-semibold text-p2p-teal">
                        See your full financial future
                      </h3>
                      <p className="text-p2p-muted text-sm">
                        Unlock 6 months of forecasting to spot problems before they happen
                      </p>
                    </div>
                    <Button className="btn-gold whitespace-nowrap">
                      Upgrade to Premium
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </div>
              )}

              {/* Forecast Upgrade Modal */}
              <UpgradeModal
                isOpen={forecastUpgradeOpen}
                onClose={() => setForecastUpgradeOpen(false)}
                triggerSource={TRIGGER_SOURCES.FORECAST}
              />
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-p2p-muted">Unable to load forecast data</p>
            </div>
          )}
        </div>
      )}
      
      {/* First-Time User Guide */}
      <GuidedHighlight 
        forceShow={forceShowGuide} 
        onComplete={() => setForceShowGuide(false)} 
      />
      
      {/* Unpaid Bills Modal */}
      <UnpaidBillsModal
        isOpen={unpaidBillsModalOpen}
        onClose={() => setUnpaidBillsModalOpen(false)}
        onBillsUpdated={refreshAllData}
      />
      
      {/* Overdue Confirmation Modal */}
      <OverdueConfirmationModal
        isOpen={overdueModalOpen}
        onClose={() => setOverdueModalOpen(false)}
        onConfirmed={handleOverdueConfirmed}
      />
    </Sidebar>
  );
}
